import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getChat, addMessageToChatStreaming, updateChatTitle, updateMessageAndTruncate } from '../../services/chatService';
import { updateMessageAndTruncateThread } from '../../services/openaiService';
import { useAuth } from '../auth/AuthContext';
import { useCompany } from '../Company/CompanyContext';
import { auth } from '../../services/firebase';
import Message from './Message';
import ChatInput from './ChatInput';
import ConversationStarters from './ConversationStarters';
import { doc, updateDoc, serverTimestamp, setDoc, collection, addDoc, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'react-hot-toast';
import { Link, useLocation } from 'react-router-dom';

import LordIcon from '../common/LordIcon';
import autorenewIcon from '../../assets/icons/autorenew.json';

// Add custom scrollbar styles
const scrollbarStyles = {
  scrollbarContainer: {
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(249, 115, 22, 0.3) rgba(249, 115, 22, 0.1)',
  },
  scrollbarWebkit: {
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'rgba(249, 115, 22, 0.1)',
      borderRadius: '10px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'rgba(249, 115, 22, 0.3)',
      borderRadius: '10px',
      border: '2px solid rgba(249, 115, 22, 0.1)',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      backgroundColor: 'rgba(249, 115, 22, 0.5)',
    },
  }
};

/**
 * Debug utility for document context issues
 * @param {Array} documents - Array of document objects from context
 * @param {String} messageText - The message being sent
 * @returns {Object} Debug information
 */
const debugDocumentContext = (documents, messageText) => {
  const debugInfo = {
    documentCount: documents?.length || 0,
    documentSizes: [],
    totalContextSize: 0,
    truncationOccurred: false,
    messageWithContext: ''
  };
  
  if (!documents || documents.length === 0) {
    console.warn('No documents provided for context debugging');
    return debugInfo;
  }
  
  // Get size info for each document
  documents.forEach((doc, index) => {
    const size = doc.pageContent?.length || 0;
    debugInfo.documentSizes.push({
      index,
      fileName: doc.metadata?.filename || 'unknown',
      contentSize: size,
      metadataSize: JSON.stringify(doc.metadata).length
    });
    debugInfo.totalContextSize += size;
  });
  
  // Build message with context (simplified version of what the main function does)
  const contextString = documents
    .map(doc => `Content from ${doc.metadata?.filename || 'document'}: ${doc.pageContent}`)
    .join('\n\n');
  
  // Check if context is too large (arbitrary limit for demonstration)
  const MAX_CONTEXT_SIZE = 100000;
  if (debugInfo.totalContextSize > MAX_CONTEXT_SIZE) {
    debugInfo.truncationOccurred = true;
    console.warn(`Context size (${debugInfo.totalContextSize}) exceeds limit (${MAX_CONTEXT_SIZE})`);
  }
  
  debugInfo.messageWithContext = `${messageText}\n\nDOCUMENT CONTEXT:\n${contextString}`;
  debugInfo.finalMessageSize = debugInfo.messageWithContext.length;
  
  console.log('Document context debug info:', debugInfo);
  return debugInfo;
};

function ChatWindow({ chatId: propChatId, contextDocuments = {}, onDocumentsUpdated }) {
  const [chat, setChat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [editingTitle, setEditingTitle] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [editingMessage, setEditingMessage] = useState(false);
  const [typing, setTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const titleInputRef = useRef(null);
  
  const { chatId: paramChatId } = useParams();
  const { currentUser } = useAuth();
  const { companyInfo } = useCompany();
  const navigate = useNavigate();
  
  // State to track pending message processing
  const [pendingMessageReady, setPendingMessageReady] = useState(false);
  const [pendingMessageData, setPendingMessageData] = useState(null);
  const [pendingMessageAttempts, setPendingMessageAttempts] = useState(0);
  
  const chatId = propChatId || paramChatId;
  
  // Fetch chat data
  useEffect(() => {
    async function fetchChat() {
      if (!chatId) return;
      
      // Check that user is authenticated
      if (!auth.currentUser) {
        setError('Authentication required. Please log in again.');
        navigate('/login');
        return;
      }
      
      try {
        setLoading(true);
        const chatData = await getChat(chatId);
        setChat(chatData);
        setNewTitle(chatData.title || 'New Chat');
        setError('');
        
        // Set loading to false before checking for pending messages
        setLoading(false);
        
        // Check for pending message in localStorage
        const pendingMessageJson = localStorage.getItem('pendingMessage');
        if (pendingMessageJson && (!chatData.messages || chatData.messages.length === 0)) {
          try {
            // Parse the message data
            const msgData = JSON.parse(pendingMessageJson);
            
            // Remove data from localStorage to prevent sending twice
            localStorage.removeItem('pendingMessage');
            
            // Check for any pending file info
            let files = [];
            const pendingFilesJson = localStorage.getItem('pendingFiles');
            if (pendingFilesJson) {
              try {
                files = JSON.parse(pendingFilesJson);
                localStorage.removeItem('pendingFiles');
              } catch (fileError) {
                console.error('Error parsing pending file data:', fileError);
              }
            }
            
            // Store the pending message data for processing
            setPendingMessageData({
              message: msgData.text,
              files: files
            });
            
            // Set the flag to start processing
            setPendingMessageReady(true);
            
          } catch (parseError) {
            console.error('Error parsing pending message:', parseError);
            // Fallback for old format
            const message = pendingMessageJson;
            localStorage.removeItem('pendingMessage');
            
            // Store as plain text
            setPendingMessageData({
              message: message,
              files: []
            });
            setPendingMessageReady(true);
          }
        }
      } catch (err) {
        console.error('Error fetching chat:', err);
        setLoading(false);
        if (err.message.includes('User must be authenticated')) {
          setError('Session expired. Please log in again.');
          navigate('/login');
        } else if (err.message.includes('Unauthorized access')) {
          setError('You do not have access to this chat.');
          navigate('/');
        } else {
          setError('Failed to load chat: ' + err.message);
        }
      }
    }
    
    fetchChat();
  }, [chatId, currentUser, navigate]);
  
  // Separate effect to handle pending message with polling
  useEffect(() => {
    // Skip if no pending message or already attempted too many times
    if (!pendingMessageReady || !pendingMessageData || pendingMessageAttempts > 10) {
      return;
    }
    
    // Function to check if chat is ready and send message
    const attemptSendPendingMessage = () => {
      // Increment attempt counter
      setPendingMessageAttempts(prev => prev + 1);
      
      // Validate chat data is fully available
      if (chat && chat.id === chatId) {
        console.log('Chat data ready, sending pending message');
        
        // Reset pending message state
        setPendingMessageReady(false);
        setPendingMessageData(null);
        
        // Send the message
        handleSendMessage(pendingMessageData.message, pendingMessageData.files);
      } else {
        console.log(`Chat data not ready yet (attempt ${pendingMessageAttempts + 1}/10)`);
        
        // If we've tried too many times, give up
        if (pendingMessageAttempts >= 9) {
          console.error('Giving up on sending pending message after too many attempts');
          setPendingMessageReady(false);
          setPendingMessageData(null);
          setError('Failed to send your message. Please try again.');
          return;
        }
        
        // Try again after increasing delay
        const delay = 500 + (pendingMessageAttempts * 300); // Increasing delay
        setTimeout(attemptSendPendingMessage, delay);
      }
    };
    
    // Start first attempt with delay
    const initialDelay = 1000;
    setTimeout(attemptSendPendingMessage, initialDelay);
    
  }, [pendingMessageReady, pendingMessageData, pendingMessageAttempts, chat, chatId]);
  
  // Listen for chat title updates from other components
  useEffect(() => {
    const handleTitleUpdate = (event) => {
      const { chatId: updatedChatId, newTitle: updatedTitle } = event.detail;
      
      // Only update if this is the current chat
      if (chatId === updatedChatId) {
        setChat(prevChat => prevChat ? { ...prevChat, title: updatedTitle } : null);
        setNewTitle(updatedTitle);
      }
    };
    
    window.addEventListener('chat-title-updated', handleTitleUpdate);
    
    return () => {
      window.removeEventListener('chat-title-updated', handleTitleUpdate);
    };
  }, [chatId]);
  
  // Focus on title input when editing
  useEffect(() => {
    if (editingTitle) {
      titleInputRef.current?.focus();
    }
  }, [editingTitle]);
  
  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
  }, [chat?.messages]);
  
  // Handle title change
  const handleTitleChange = async () => {
    if (!newTitle.trim()) {
      setNewTitle(chat.title || 'New Chat');
      setEditingTitle(false);
      return;
    }
    
    try {
      // Check authentication
      if (!auth.currentUser) {
        setError('Authentication required. Please log in again.');
        navigate('/login');
        return;
      }
      
      await updateChatTitle(chatId, newTitle.trim());
      setChat(prev => ({
        ...prev,
        title: newTitle.trim()
      }));
      setEditingTitle(false);
      
      // Dispatch custom event for other components to update
      const titleUpdateEvent = new CustomEvent('chat-title-updated', { 
        detail: {
          chatId,
          newTitle: newTitle.trim()
        }
      });
      window.dispatchEvent(titleUpdateEvent);
      
    } catch (err) {
      console.error('Error updating chat title:', err);
      if (err.message.includes('User must be authenticated')) {
        setError('Session expired. Please log in again.');
        navigate('/login');
      } else {
        // Revert to original title
        setNewTitle(chat.title || 'New Chat');
        setEditingTitle(false);
      }
    }
  };
  
  // Function to create a user message
  const createUserMessage = (query, fileContent) => {
    // Set initial message data
    const newUserMessage = {
      id: uuidv4(),
      role: 'user',
      content: [
        {
          type: 'text',
          text: { value: query },
        },
      ],
      timestamp: new Date(),
    };

    // If file content is available, add it to the message
    if (fileContent) {
      newUserMessage.fileContent = fileContent;
    }

    // Detect if this is a Calendar or Gmail related query
    const lowerQuery = query.toLowerCase();
    const isCalendarQuery = 
      lowerQuery.includes('calendar') || 
      lowerQuery.includes('schedule') || 
      lowerQuery.includes('meeting') || 
      lowerQuery.includes('appointment') ||
      lowerQuery.includes('event');
      
    const isGmailQuery = 
      lowerQuery.includes('email') || 
      lowerQuery.includes('gmail') || 
      lowerQuery.includes('mail') ||
      lowerQuery.includes('inbox') ||
      lowerQuery.includes('message') ||
      lowerQuery.includes('send') ||
      lowerQuery.includes('received');

    // Set tool type based on query content
    if (isCalendarQuery && isGmailQuery) {
      newUserMessage.toolType = 'calendar+gmail';
    } else if (isCalendarQuery) {
      newUserMessage.toolType = 'calendar';
    } else if (isGmailQuery) {
      newUserMessage.toolType = 'gmail';
    }

    // Set initial assistant message based on tool type
    let assistantMessage = "I'll help you with that.";
    if (newUserMessage.toolType === 'calendar+gmail') {
      assistantMessage = "I'll check your calendar and emails for that information.";
    } else if (newUserMessage.toolType === 'calendar') {
      assistantMessage = "I'll check your calendar for that information.";
    } else if (newUserMessage.toolType === 'gmail') {
      assistantMessage = "I'll check your emails for that information.";
    }

    newUserMessage.assistantMessage = assistantMessage;

    return newUserMessage;
  };

  // Add a dedicated function for building context string from documents
  const buildContextString = (contextDocuments = {}) => {
    if (!contextDocuments || Object.keys(contextDocuments).length === 0) {
      return '';
    }

    let contextString = '--- DOCUMENT CONTEXT ---\n\n';
    
    // Process each category of documents
    Object.keys(contextDocuments).forEach(category => {
      const docsInCategory = contextDocuments[category] || [];
      
      if (docsInCategory.length > 0) {
        // Add category header
        contextString += `## ${category.toUpperCase()} DOCUMENTS:\n`;
        
        // Add each document with its content
        docsInCategory.forEach((doc, index) => {
          contextString += `### Document ${index + 1}: ${doc.name}\n`;
          contextString += `${doc.content}\n\n`;
        });
        
        contextString += '\n';
      }
    });
    
    contextString += '--- END DOCUMENT CONTEXT ---\n\n';
    
    // Truncate if too large (100,000 chars max to prevent context overflow)
    const maxContextLength = 100000;
    if (contextString.length > maxContextLength) {
      contextString = contextString.substring(0, maxContextLength);
      contextString += '\n[Document context truncated due to size limitations]';
    }
    
    return contextString;
  };

  // Add a debug utility for tracking context usage
  const debugDocumentContext = (documents = [], originalMessage = '') => {
    const result = {
      documentCount: documents.length,
      documentSizes: [],
      totalDocumentSize: 0,
      totalContextSize: 0,
      messageSize: originalMessage.length,
      truncationOccurred: false
    };
    
    if (!documents || documents.length === 0) {
      return result;
    }
    
    // Calculate statistics
    documents.forEach(doc => {
      const contentSize = doc.content ? doc.content.length : 0;
      result.documentSizes.push({
        name: doc.name,
        size: contentSize
      });
      result.totalDocumentSize += contentSize;
    });
    
    // Calculate context string size
    const fullContextString = buildContextString({ all: documents });
    result.totalContextSize = fullContextString.length;
    
    // Check if truncation occurred
    result.truncationOccurred = result.totalContextSize > 100000;
    
    return result;
  };

  // Debug function to test document context
  const testDocumentContext = () => {
    const contextString = buildContextString(contextDocuments);
    
    if (contextString) {
      console.log('=== DOCUMENT CONTEXT TEST ===');
      console.log('Context documents available:');
      
      Object.keys(contextDocuments).forEach(type => {
        const docs = contextDocuments[type] || [];
        if (docs.length > 0) {
          console.log(`- ${type}: ${docs.length} document(s)`);
          docs.forEach((doc, i) => {
            console.log(`  ${i+1}. ${doc.name} (${doc.content ? doc.content.length : 0} chars)`);
          });
        }
      });
      
      console.log('\nGenerated context string:');
      console.log(contextString);
      console.log('=== END TEST ===');
      
      // Add a test message to the chat
      const testMessage = {
        id: 'debug-context',
        role: 'assistant',
        content: [{ 
          type: 'text', 
          text: { value: `DEBUG: Found ${Object.values(contextDocuments).flat().length} context document(s). Context string is ${contextString.length} characters. This message will automatically disappear in 5 seconds.` } 
        }],
        isDebug: true,
        timestamp: new Date()
      };
      
      setChat(prevChat => ({
        ...prevChat,
        messages: [...(prevChat?.messages || []), testMessage]
      }));
      
      // Remove debug message after 5 seconds
      setTimeout(() => {
        setChat(prevChat => ({
          ...prevChat,
          messages: prevChat.messages.filter(msg => msg.id !== 'debug-context')
        }));
      }, 5000);
      
      return true;
    } else {
      console.log('=== DOCUMENT CONTEXT TEST ===');
      console.log('No context documents available.');
      console.log('=== END TEST ===');
      
      // Add a test message to the chat
      const testMessage = {
        id: 'debug-context',
        role: 'assistant',
        content: [{ 
          type: 'text', 
          text: { value: 'DEBUG: No document context found. Please upload documents in the document sections (Scorecard, VTO, Rocks). This message will automatically disappear in 5 seconds.' } 
        }],
        isDebug: true,
        timestamp: new Date()
      };
      
      setChat(prevChat => ({
        ...prevChat,
        messages: [...(prevChat?.messages || []), testMessage]
      }));
      
      // Remove debug message after 5 seconds
      setTimeout(() => {
        setChat(prevChat => ({
          ...prevChat,
          messages: prevChat.messages.filter(msg => msg.id !== 'debug-context')
        }));
      }, 5000);
      
      return false;
    }
  };

  // Update documents when they change
  useEffect(() => {
    // If documents are updated externally, refresh local state
    if (contextDocuments && Object.keys(contextDocuments).length > 0) {
      console.log('Context documents updated externally, refreshing local state');
      
      // Optionally notify parent of change
      if (onDocumentsUpdated) {
        onDocumentsUpdated(contextDocuments);
      }
    }
  }, [contextDocuments, onDocumentsUpdated]);

  // Handle sending a message with streaming
  async function handleSendMessage(message, files = []) {
    if (!message.trim() || !chatId || sending) {
      console.log('Message validation failed: empty message, missing chatId, or already sending');
      return;
    }
    
    // Special command to test document context
    if (message.trim().toLowerCase() === '/test-context') {
      testDocumentContext();
      return;
    }
    
    // Safety check - ensure chat is available
    if (!chat || !chat.id) {
      console.error("Chat data not available yet");
      setError('Chat not fully loaded. Please try again in a moment.');
      return;
    }
    
    console.log('Sending message to chat:', chat.id);
    
    // Check authentication
    if (!auth.currentUser) {
      setError('Authentication required. Please log in again.');
      navigate('/login');
      return;
    }
    
    try {
      // First, fetch any documents from Firestore that might be relevant
      let firestoreDocuments = { ...contextDocuments };
      
      // Determine if we need to fetch company-specific documents
      const companyId = companyInfo?.id;
      
      if (companyId) {
        try {
          // Query documents by company ID
          const documentsRef = collection(db, 'documents');
          const q = query(documentsRef, where('companyId', '==', companyId));
          const querySnapshot = await getDocs(q);
          
          // Process query results
          if (!querySnapshot.empty) {
            const docs = [];
            
            querySnapshot.forEach((docSnapshot) => {
              const data = docSnapshot.data();
              docs.push({
                id: docSnapshot.id,
                name: data.name,
                content: data.content,
                pageContent: data.content,
                metadata: {
                  filename: data.name,
                  filetype: data.type,
                  filesize: data.size,
                  category: data.category
                },
                category: data.category
              });
            });
            
            // Organize by category
            docs.forEach(doc => {
              const category = doc.category || 'general';
              if (!firestoreDocuments[category]) {
                firestoreDocuments[category] = [];
              }
              
              // Add document if not already present (by ID)
              if (!firestoreDocuments[category].find(existingDoc => existingDoc.id === doc.id)) {
                firestoreDocuments[category].push(doc);
              }
            });
            
            console.log(`Fetched ${docs.length} documents from Firestore for company ${companyId}`);
          }
        } catch (docError) {
          console.error('Error fetching documents from Firestore:', docError);
          // Continue with existing documents even if fetching fails
        }
      }
      
      // Create the messages
      const userMessage = createUserMessage(message, files.length > 0 ? files[0] : null);
      
      // Add them to the message list
      setChat(prevChat => ({
        ...prevChat,
        messages: [...(prevChat?.messages || []), userMessage]
      }));
      
      // Set sending state but don't disable the input completely
      setSending(true);
      
      // Keep the focus on the input by setting a small delay to scroll
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
      
      // Add context documents to message if available
      const contextString = buildContextString(firestoreDocuments);
      const messageWithContext = contextString ? `${message}\n\n${contextString}` : message;
      
      // Use our debug utility to get detailed context information
      const debugInfo = debugDocumentContext(
        Object.values(firestoreDocuments).flat(),
        message
      );
      
      // Log debug information
      console.log('Document Context Debug:', debugInfo);
      
      // Log for debugging
      if (contextString) {
        console.log(`Including ${contextString.length} chars of document context with message`);
        console.log(`Message with context total length: ${messageWithContext.length} chars`);
      } else {
        console.log('No document context included with message');
      }
      
      // Define the stream update handler with improved handling for real-time updates
      const handleStreamingMessage = async (assistantMessage, isFinal = false) => {
        console.log('Stream update received in UI:', isFinal, assistantMessage);
        
        // Validate content structure before updating UI
        if (assistantMessage && assistantMessage.content) {
          let formattedContent = assistantMessage.content;
          
          // Ensure content is an array and all items have proper structure
          if (Array.isArray(formattedContent)) {
            // Filter out any null/undefined items
            formattedContent = formattedContent
              .filter(item => item !== null && item !== undefined)
              .map(item => {
                if (item && item.type === 'text') {
                  return {
                    type: 'text',
                    text: typeof item.text === 'object' 
                      ? item.text 
                      : { value: typeof item.text === 'string' ? item.text : 'No content' }
                  };
                }
                return item;
              });
          } else if (formattedContent) {
            // If content is not an array but exists, convert to proper format
            formattedContent = [{
              type: 'text',
              text: { value: 'Response received in unexpected format' }
            }];
          } else {
            // Fallback for empty content
            formattedContent = [{
              type: 'text',
              text: { value: 'No response content' }
            }];
          }
          
          // Check if there are any file annotations in the content
          const hasFileAttachments = formattedContent.some(item => 
            item.type === 'text' && 
            item.text && 
            item.text.annotations && 
            item.text.annotations.some(ann => 
              ann.type === 'file_path' && ann.file_path && ann.file_path.file_id
            )
          );
          
          // Check for attachments in the message object
          const hasAttachments = assistantMessage.attachments && 
            assistantMessage.attachments.length > 0 &&
            assistantMessage.attachments.some(att => att.file_id);
          
          // Extract all file IDs from annotations and attachments
          const fileIds = [];
          
          // Check for file_path annotations
          formattedContent.forEach(item => {
            if (item.type === 'text' && item.text && item.text.annotations) {
              item.text.annotations.forEach(ann => {
                if (ann.type === 'file_path' && ann.file_path && ann.file_path.file_id) {
                  fileIds.push(ann.file_path.file_id);
                  
                  // Log detection of file path annotation
                  console.log('Found file path annotation:', ann.file_path.file_id, ann.text);
                }
              });
            }
          });
          
          // Check for attachments
          if (assistantMessage.attachments) {
            assistantMessage.attachments.forEach(att => {
              if (att.file_id && !fileIds.includes(att.file_id)) {
                fileIds.push(att.file_id);
                
                // Log detection of attachment
                console.log('Found file attachment:', att.file_id);
              }
            });
          }
          
          // Create a properly formatted message
          const formattedMessage = {
            ...assistantMessage,
            content: formattedContent,
            isStreaming: !isFinal,
            // Use the assistantMessage's toolType directly if provided
            toolType: assistantMessage.toolType
          };
          
          // Add file IDs to the message if any were found
          if (fileIds.length > 0) {
            formattedMessage.fileIds = fileIds;
            
            // Ensure we preserve any annotations in the content for file path references
            formattedMessage.hasFiles = true;
            
            // Log that we found files
            console.log(`Found ${fileIds.length} file(s) in message:`, fileIds);
          }
          
          // If there were attachments in the original message, preserve them
          if (assistantMessage.attachments && assistantMessage.attachments.length > 0) {
            formattedMessage.attachments = assistantMessage.attachments;
          }
          
          // Track when calendar tool is used
          if (assistantMessage.toolType === 'calendar' || assistantMessage.toolType === 'calendar+gmail') {
            formattedMessage.calendarToolUsed = true;
            console.log('Calendar tool used, adding flag to message');
          }
          
          // Find the matching user message to inherit toolType if needed
          setChat(prevChat => {
            if (!prevChat || !prevChat.messages) return prevChat;
            
            const updatedMessages = [...prevChat.messages];
            
            // Find the last user message to get its toolType if needed
            const lastUserMessage = [...updatedMessages].reverse().find(msg => msg.role === 'user');
            const userToolType = lastUserMessage?.toolType;
            
            // If the assistantMessage doesn't have a toolType, inherit from user
            if (!formattedMessage.toolType && userToolType) {
              formattedMessage.toolType = userToolType;
            }
            
            // Check if the last message is from the assistant
            const lastMessage = updatedMessages[updatedMessages.length - 1];
            if (lastMessage && lastMessage.role === 'assistant') {
              // Replace only if the last message is already an assistant message
              updatedMessages[updatedMessages.length - 1] = formattedMessage;
            } else {
              // Otherwise append the new assistant message
              updatedMessages.push(formattedMessage);
            }
            
            return {
              ...prevChat,
              messages: updatedMessages
            };
          });
        } else {
          console.error('Received invalid message structure:', assistantMessage);
        }
        
        // Handle saving the final message to Firestore directly
        if (isFinal) {
          console.log('Final message received, ending sending state');
          setSending(false);
          
          // Save the modified message with the calendarToolUsed flag if applicable
          try {
            // Only proceed if we have a valid message with content
            if (assistantMessage && Array.isArray(assistantMessage.content) && assistantMessage.content.length > 0) {
              const chatRef = doc(db, 'chats', chatId);
              
              // Get the current chat document
              const updatedMessages = [...chat.messages];
              const assistantMessageIndex = updatedMessages.length - 1;
              
              if (assistantMessageIndex >= 0 && updatedMessages[assistantMessageIndex].role === 'assistant') {
                // Create a properly formatted message to save
                const messageToSave = {
                  ...updatedMessages[assistantMessageIndex],
                  content: assistantMessage.content, // Use assistantMessage.content instead of formattedContent
                  isStreaming: false,
                  timestamp: new Date(),
                  toolType: assistantMessage.toolType
                };
                
                // Add calendar tool flag if needed
                if (assistantMessage.toolType === 'calendar' || assistantMessage.toolType === 'calendar+gmail') {
                  messageToSave.calendarToolUsed = true;
                }
                
                // Update the assistant message in the messages array
                updatedMessages[assistantMessageIndex] = messageToSave;
                
                // Update the Firebase document with the complete message set
                updateDoc(chatRef, {
                  messages: updatedMessages,
                  lastMessage: messageToSave,
                  updatedAt: serverTimestamp()
                })
                .then(() => {
                  console.log('Successfully saved message with calendarToolUsed flag to Firebase');
                })
                .catch(err => {
                  console.error('Error updating chat in Firebase:', err);
                });
              }
            }
          } catch (err) {
            console.error('Error saving message to Firebase:', err);
          }
        }
        
        // Always scroll to the latest message
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
        }, 100);
      };
      
      // Send the message to your API for streaming response
      // Note: The message already contains the extracted text, no need to upload files
      const result = await addMessageToChatStreaming(
        chatId,
        message,
        companyInfo,
        handleStreamingMessage,
        chat.messages.length === 0
      );
      
      // Handle title update if this was the first message
      if (result && result.newTitle) {
        setChat(prevChat => ({
          ...prevChat,
          title: result.newTitle
        }));
        setNewTitle(result.newTitle);
        
        // Store the chat ID in localStorage to ensure the sidebar sees this new chat
        localStorage.setItem('justCreatedChatId', chatId);
      }
      
    } catch (error) {
      console.error('Error sending message:', error);
      setSending(false);
      
      if (error.message.includes('User must be authenticated')) {
        setError('Session expired. Please log in again.');
        navigate('/login');
      } else {
        setError('Failed to send message: ' + error.message);
      }
    }
  }
  
  // Handle message editing
  const handleEditMessage = async (messageIndex, newContent) => {
    if (!chat || !chat.messages || messageIndex >= chat.messages.length) {
      return;
    }
    
    try {
      // Check authentication
      if (!auth.currentUser) {
        setError('Authentication required. Please log in again.');
        navigate('/login');
        return;
      }
      
      setSending(true);
      setEditingMessage(true);
      
      // Get the messages from the chat
      const messages = [...chat.messages];
      
      // Get the message to edit
      const messageToEdit = messages[messageIndex];
      
      // Update the content of the message being edited in our local state
      const updatedMessage = {
        ...messageToEdit,
        content: newContent,
        edited: true,
        editedAt: new Date()
      };
      
      // Get all messages up to and including the edited message
      const messagesUpToEdit = messages.slice(0, messageIndex + 1);
      messagesUpToEdit[messageIndex] = updatedMessage;
      
      // Create initial assistant message for streaming
      const initialAssistantMessage = {
        role: 'assistant',
        content: [{ type: 'text', text: { value: 'Thinking...' } }],
        timestamp: new Date()
      };
      
      // Update UI with truncated messages + thinking indicator
      setChat(prevChat => ({
        ...prevChat,
        messages: [...messagesUpToEdit, initialAssistantMessage]
      }));
      
      // Update the message in Firestore first, truncating subsequent messages
      await updateMessageAndTruncate(chatId, messageIndex, newContent);
      
      // Update the OpenAI thread using our new method
      const result = await updateMessageAndTruncateThread(
        chat.threadId,
        messageToEdit,
        newContent,
        messageIndex,
        companyInfo
      );
      
      // Define the stream update handler for the AI response
      const handleStreamingMessage = (assistantMessage, isFinal = false) => {
        console.log('Stream update received in UI (after edit):', isFinal, assistantMessage);
        
        setChat(prevChat => {
          if (!prevChat || !prevChat.messages) return prevChat;
          
          const updatedMessages = [...prevChat.messages];
          
          // Replace the last message (assistant) with the updated streaming message
          if (updatedMessages.length > 0) {
            // Ensure the content format is consistent
            let formattedContent = assistantMessage.content;
            
            // Format text content if needed
            if (formattedContent && formattedContent.length > 0) {
              formattedContent = formattedContent.map(item => {
                if (item.type === 'text') {
                  return {
                    type: 'text',
                    text: typeof item.text === 'object' ? item.text : { value: item.text }
                  };
                }
                return item;
              });
            }
            
            // Get the user's toolType from the original message if it exists
            const userMessage = updatedMessages[updatedMessages.length - 2]; // User message is right before assistant
            const userToolType = userMessage?.toolType;
            
            // Create a properly formatted message
            const formattedMessage = {
              ...assistantMessage,
              content: formattedContent,
              isStreaming: !isFinal, // Explicitly set isStreaming based on isFinal parameter
              // Use the assistant's toolType if provided, or inherit from user message
              toolType: assistantMessage.toolType || userToolType
            };
            
            // Add calendar tool flag if needed
            if (formattedMessage.toolType === 'calendar' || formattedMessage.toolType === 'calendar+gmail') {
              formattedMessage.calendarToolUsed = true;
            }
            
            updatedMessages[updatedMessages.length - 1] = formattedMessage;
          }
          
          return {
            ...prevChat,
            messages: updatedMessages
          };
        });
        
        // If this is the final message, end the sending state
        if (isFinal) {
          console.log('Final message received, ending sending state');
          setSending(false);
          setEditingMessage(false);
          
          // Save the final message to Firestore directly rather than calling addMessageToChatStreaming
          // This avoids trying to add a message to a thread while a run is active
          // We'll update the messages array in the chat document
          try {
            // Only proceed if we have a valid message with content
            if (assistantMessage && Array.isArray(assistantMessage.content) && assistantMessage.content.length > 0) {
              // Filter out any null/undefined items and ensure proper structure
              const validContent = assistantMessage.content
                .filter(item => item !== null && item !== undefined)
                .map(item => {
                  if (item && item.type === 'text') {
                    // Ensure text field has proper structure and no undefined values
                    return {
                      type: 'text',
                      text: {
                        value: (item.text && item.text.value) 
                          ? item.text.value 
                          : (typeof item.text === 'string' ? item.text : 'No response content')
                      }
                    };
                  }
                  return item;
                });
              
              // Only save if we have valid content after filtering
              if (validContent.length > 0) {
                // Create a properly formatted message to save with no undefined values
                const messageToSave = {
                  role: 'assistant',
                  content: validContent,
                  timestamp: new Date()
                };
                
                // Create a clean version of messagesUpToEdit with no undefined values
                const cleanMessagesUpToEdit = messagesUpToEdit.map(msg => {
                  // Deep clone the message to avoid undefined values
                  const cleanMsg = JSON.parse(JSON.stringify({
                    role: msg.role,
                    content: typeof msg.content === 'string' 
                      ? msg.content 
                      : (Array.isArray(msg.content) ? msg.content : []),
                    timestamp: msg.timestamp
                  }));
                  
                  // Add edited flags if present and not undefined
                  if (msg.edited) cleanMsg.edited = true;
                  if (msg.editedAt) cleanMsg.editedAt = msg.editedAt;
                  
                  return cleanMsg;
                });
                
                const updatedMessages = [...cleanMessagesUpToEdit, messageToSave];
                
                // Update the chat document with the complete message set
                updateDoc(doc(db, 'chats', chatId), {
                  messages: updatedMessages,
                  updatedAt: serverTimestamp()
                }).then(() => {
                  console.log('Successfully updated chat with edited message and response');
                }).catch(updateError => {
                  console.error('Error updating Firestore after edit:', updateError);
                  setError('Failed to save response: ' + updateError.message);
                });
              } else {
                console.error('No valid content items after filtering');
                setError('Failed to save response: Content validation failed');
              }
            } else {
              console.error('Cannot save message - invalid message structure:', 
                assistantMessage ? `Has content: ${!!assistantMessage.content}, Is array: ${Array.isArray(assistantMessage.content)}` : 'Message is undefined');
              setError('Failed to save response: Invalid message structure');
            }
          } catch (err) {
            console.error('Error preparing chat update after edit:', err);
            setError('Failed to save edited message: ' + err.message);
          }
        }
        
        // Always scroll to the latest message
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
        }, 100);
      };
      
      // Process the response with streaming
      await result.continueConversation(handleStreamingMessage);
      
    } catch (error) {
      console.error('Error editing message:', error);
      setSending(false);
      setEditingMessage(false);
      
      if (error.message.includes('User must be authenticated')) {
        setError('Session expired. Please log in again.');
        navigate('/login');
      } else {
        setError('Failed to edit message: ' + error.message);
      }
    }
  };
  
  // Function to update an assistant message with new content
  const updateAssistantMessage = (messageId, updates) => {
    setChat(prevChat => ({
      ...prevChat,
      messages: prevChat.messages.map(msg => 
        msg.id === messageId 
          ? { ...msg, ...updates } 
          : msg
      )
    }));
  };

  // Stream the assistant's response
  const streamAssistantResponse = async (userMessage, toolType = null) => {
    try {
      let endpoint = '/api/chat';
      let payload = {
        messages: chat.messages.concat(userMessage).map((msg) => ({
          role: msg.role,
          content: msg.content,
        })),
      };

      // Add specific tool data based on tool type
      if (toolType === 'calendar') {
        endpoint = '/api/calendar-query';
        payload = { 
          query: userMessage.content[0].text.value,
          messages: chat.messages.map((msg) => ({
            role: msg.role,
            content: msg.content,
          }))
        };
      } else if (toolType === 'gmail') {
        endpoint = '/api/gmail-query';
        payload = { 
          query: userMessage.content[0].text.value,
          messages: chat.messages.map((msg) => ({
            role: msg.role,
            content: msg.content,
          }))
        };
      } else if (toolType === 'calendar+gmail') {
        endpoint = '/api/combined-query';
        payload = { 
          query: userMessage.content[0].text.value,
          messages: chat.messages.map((msg) => ({
            role: msg.role,
            content: msg.content,
          }))
        };
      }

      if (userMessage.fileContent) {
        payload.fileContent = userMessage.fileContent;
      }

      // Make the API request
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.statusText}`);
      }

      if (toolType) {
        // Handle streaming response for tool-based queries
        const reader = response.body.getReader();
        let accumulatedContent = '';
        
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) {
            // Process final response
            handleCompletedResponse(accumulatedContent, toolType);
            break;
          }
          
          // Decode and accumulate the chunk
          const chunk = new TextDecoder().decode(value);
          accumulatedContent += chunk;
          
          // Update UI with accumulated content
          updateAssistantMessage(toolType, accumulatedContent);
        }
      } else {
        // Handle regular response
        const data = await response.json();
        updateAssistantMessage(data.content);
      }
    } catch (error) {
      console.error('Error in streamAssistantResponse:', error);
      updateAssistantMessage(
        "I'm sorry, I encountered an error while processing your request. Please try again."
      );
    }
  };

  // Function to handle completed response
  const handleCompletedResponse = (response, toolType) => {
    let finalResponse = response;
    
    // Don't detect based on content - only use the explicit toolType
    
    // Update assistant message with appropriate content and tool type
    setChat(prevChat => {
      const updatedMessages = [...prevChat.messages];
      const assistantMessageIndex = updatedMessages.length - 1;
      
      if (assistantMessageIndex >= 0 && updatedMessages[assistantMessageIndex].role === 'assistant') {
        // Only use the toolType passed from the backend, don't try to detect from content
        const updatedMessage = {
          ...updatedMessages[assistantMessageIndex],
          content: [
            {
              type: 'text',
              text: { value: finalResponse },
            },
          ],
          isStreaming: false,
          toolType: toolType,  // Use the toolType directly as provided
        };
        
        // Add calendar tool flag if needed
        if (toolType === 'calendar' || toolType === 'calendar+gmail') {
          updatedMessage.calendarToolUsed = true;
        }
        
        updatedMessages[assistantMessageIndex] = updatedMessage;
      }
      
      return {
        ...prevChat,
        messages: updatedMessages
      };
    });
  };
  
  // In the handleFilesUploaded function, add context document support
  // Find the handleFilesUploaded function and update it to include context
  async function handleFilesUploaded(files) {
    console.log('Files uploaded:', files);
    
    if (!files || files.length === 0) {
      console.log('No files provided');
      return;
    }
    
    if (!chatId) {
      setError('Chat ID not available');
      return;
    }
    
    if (!auth.currentUser) {
      setError('Authentication required. Please log in again.');
      navigate('/login');
      return;
    }
    
    // Set uploading state
    setTyping(true);
    
    try {
      // Create a descriptive message about the files
      let fileTypes = files.map(file => file.type).reduce((acc, type) => {
        acc[type] = (acc[type] || 0) + 1;
        return acc;
      }, {});
      
      let fileDescriptions = [];
      if (fileTypes['application/pdf'] > 0) {
        fileDescriptions.push(`${fileTypes['application/pdf']} PDF${fileTypes['application/pdf'] > 1 ? 's' : ''}`);
      }
      if (Object.keys(fileTypes).some(type => type.startsWith('image/'))) {
        const imageCount = Object.keys(fileTypes)
          .filter(type => type.startsWith('image/'))
          .reduce((sum, type) => sum + fileTypes[type], 0);
        fileDescriptions.push(`${imageCount} image${imageCount > 1 ? 's' : ''}`);
      }
      if (fileTypes['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'] > 0 || 
          fileTypes['application/vnd.ms-excel'] > 0) {
        const spreadsheetCount = (fileTypes['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'] || 0) + 
                                (fileTypes['application/vnd.ms-excel'] || 0);
        fileDescriptions.push(`${spreadsheetCount} spreadsheet${spreadsheetCount > 1 ? 's' : ''}`);
      }
      
      // Add a generic count for other file types
      const countedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 
                            'application/vnd.ms-excel'].concat(
                              Object.keys(fileTypes).filter(type => type.startsWith('image/'))
                            );
      const otherCount = files.length - countedTypes.reduce((sum, type) => sum + (fileTypes[type] || 0), 0);
      if (otherCount > 0) {
        fileDescriptions.push(`${otherCount} other file${otherCount > 1 ? 's' : ''}`);
      }
      
      const fileDescription = fileDescriptions.join(', ');
      
      // Create prompt for the AI based on file types
      let message = `I've uploaded ${files.length} file${files.length > 1 ? 's' : ''} (${fileDescription}). `;
      
      if (files.some(file => file.type === 'application/pdf')) {
        message += 'For PDFs, please extract and analyze the key information. ';
      }
      if (files.some(file => file.type.startsWith('image/'))) {
        message += 'For images, please describe what you see and extract any text content. ';
      }
      if (files.some(file => 
        file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
        file.type === 'application/vnd.ms-excel'
      )) {
        message += 'For spreadsheets, please analyze the data and provide insights. ';
      }
      
      message += 'Please confirm what you find in these files.';
      
      // Add loading message
      const temporaryId = `temp-${Date.now()}`;
      const userMessage = {
        id: temporaryId,
        role: 'user',
        content: [{ 
          type: 'text', 
          text: { value: message } 
        }],
        timestamp: new Date()
      };
      
      setChat(prevChat => ({
        ...prevChat,
        messages: [...prevChat.messages, userMessage]
      }));
      
      // Get token
      const token = await auth.currentUser.getIdToken();
      
      // First, save documents to Firestore
      const userId = auth.currentUser.uid;
      const companyId = companyInfo?.id;
      
      if (!companyId) {
        console.warn('No company ID found, documents will be saved at user level only');
      }
      
      // Create documents array for storage
      const documentsToStore = [];
      
      // Process each file to extract content
      for (const file of files) {
        // First, determine the appropriate category for the file
        let category = 'general'; // Default category
        
        // Simple categorization based on filename
        const lowerName = file.name.toLowerCase();
        if (lowerName.includes('scorecard') || lowerName.includes('score') || lowerName.includes('metrics')) {
          category = 'scorecard';
        } else if (lowerName.includes('vto') || lowerName.includes('vision') || lowerName.includes('traction')) {
          category = 'vto';
        } else if (lowerName.includes('rock') || lowerName.includes('goal') || lowerName.includes('objective')) {
          category = 'rocks';
        } else if (lowerName.includes('accountability') || lowerName.includes('a/c') || lowerName.includes('org chart') || lowerName.includes('structure')) {
          category = 'accountability_chart';
        } else if (lowerName.includes('core process') || lowerName.includes('process') || lowerName.includes('procedure') || lowerName.includes('workflow')) {
          category = 'core_process';
        }
        
        // Store document info for Firestore
        documentsToStore.push({
          name: file.name,
          type: file.type,
          size: file.size,
          category: category,
          content: file.text || '', // The extracted text from FileUpload component
          createdAt: new Date(),
          userId: userId,
          companyId: companyId || null,
          chatId: chatId
        });
      }
      
      // Save documents to Firestore
      const savedDocRefs = [];
      const documentsCollection = collection(db, 'documents');
      
      // Add each document to Firestore
      for (const docData of documentsToStore) {
        try {
          const docRef = await addDoc(documentsCollection, docData);
          savedDocRefs.push({
            id: docRef.id,
            ...docData
          });
          console.log('Document written with ID: ', docRef.id);
        } catch (e) {
          console.error('Error adding document: ', e);
          // Continue with other documents even if one fails
        }
      }
      
      console.log('Documents saved to Firestore:', savedDocRefs);
      
      // Update local contextDocuments object with new documents
      const updatedContextDocuments = { ...contextDocuments };
      
      // Group by category
      savedDocRefs.forEach(doc => {
        if (!updatedContextDocuments[doc.category]) {
          updatedContextDocuments[doc.category] = [];
        }
        
        // Add to appropriate category with correct format
        updatedContextDocuments[doc.category].push({
          id: doc.id,
          name: doc.name,
          content: doc.content,
          pageContent: doc.content, // For compatibility with existing code
          metadata: {
            filename: doc.name,
            filetype: doc.type,
            filesize: doc.size,
            category: doc.category
          }
        });
      });
      
      // Now with updated documents, create the context string
      const contextString = buildContextString(updatedContextDocuments);
      const messageWithContext = contextString ? `${message}\n\n${contextString}` : message;
      
      // Create form data
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });
      
      // Add chat ID and message text
      formData.append('chatId', chatId);
      formData.append('message', messageWithContext); // Using message with context
      formData.append('documentIds', JSON.stringify(savedDocRefs.map(doc => doc.id)));
      
      // Send the request
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to upload files');
      }
      
      const responseData = await response.json();
      
      // Remove temporary user message and add official one with server ID
      setChat(prevChat => {
        const messages = prevChat.messages.filter(msg => msg.id !== temporaryId);
        return {
          ...prevChat,
          messages: [
            ...messages, 
            {
              ...userMessage,
              id: responseData.messageId
            },
            {
              id: responseData.aiMessageId,
              role: 'assistant',
              content: [{ 
                type: 'text', 
                text: { value: responseData.aiMessage } 
              }],
              timestamp: new Date()
            }
          ]
        };
      });
      
      // Update the context documents in the parent component if possible
      if (typeof onDocumentsUpdated === 'function') {
        onDocumentsUpdated(updatedContextDocuments);
      }
      
      console.log('Files processed successfully:', responseData);
    } catch (error) {
      console.error('Error uploading files:', error);
      setError(error.message);
    } finally {
      setTyping(false);
    }
  }
  
  if (loading) {
    return (
      <div className="flex flex-col h-full items-center justify-center">
        <div className="animate-pulse-slow rounded-full h-16 w-16 border-t-2 border-b-2 border-orange-500 mb-4"></div>
        <p className="text-orange-700 font-medium">Loading conversation...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="flex flex-col h-full items-center justify-center">
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded-lg shadow transition-all duration-300 hover:shadow-md">
          <p className="font-bold font-heading">Error</p>
          <p>{error}</p>
        </div>
        <button 
          onClick={() => window.location.reload()} 
          className="mt-4 px-6 py-2 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-all duration-300 flex items-center"
        >
          <LordIcon 
            icon={autorenewIcon} 
            size={24} 
            trigger="hover"
            colors="primary:#ffffff"
            className="mr-2"
          />
          Retry
        </button>
      </div>
    );
  }
  
  return (
    <div className="flex flex-col h-full">
      {/* Main chat content */}
      <div 
        className="flex-1 overflow-y-auto px-4 bg-white"
        style={{
          ...scrollbarStyles.scrollbarContainer,
          ...scrollbarStyles.scrollbarWebkit
        }}
      >
        {error && (
          <div className="p-4 m-4 bg-red-50 text-red-700 rounded-xl border border-red-200">
            {error}
          </div>
        )}
        
        {editingMessage && (
          <div className="p-4 m-4 bg-orange-50 text-orange-700 rounded-xl border border-orange-200">
            <div className="flex items-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-orange-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>Updating conversation with the edited message...</span>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center h-full">
            <div className="animate-pulse-slow text-orange-600">
              <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
          </div>
        ) : (
          <>
            {(!chat?.messages || chat.messages.length === 0) && (
              <div className="my-8">
                <ConversationStarters onStartConversation={handleSendMessage} />
              </div>
            )}

            {chat?.messages && chat.messages.length > 0 && (
              <div className="py-6 space-y-6">
                {chat.messages.map((message, index) => (
                  <div key={index} className="message-container">
                    <Message
                      message={message}
                      isUser={message.role === 'user'}
                      onEditMessage={handleEditMessage}
                      messageIndex={index}
                      isPreviousFromSameRole={index > 0 ? chat.messages[index - 1]?.role === message.role : false}
                    />
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Chat input - fixed at bottom */}
      <div className="flex-shrink-0 border-t border-gray-100 p-4 pb-safe">
        <ChatInput
          onSendMessage={handleSendMessage}
          disabled={loading || editingMessage}
          isSending={sending}
        />
        {sending && (
          <div className="text-xs text-orange-500 mt-1 flex items-center">
            <span className="animate-pulse mr-1">●</span> AI is responding...
          </div>
        )}
      </div>
    </div>
  );
}

export default ChatWindow;
